import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import MenuTop from '../../components/menu/top/menu-top';
import './style.css';
import  { fetchProjectsGroupedService } from '../../features/project/service/project-service';
import Loader from '../../components/loader/loader';
import AccordionComponent from '../../components/accordion/accordion';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/inputs/common/input';
import storage from '../../shared/services/storage';
import { fetchUserService } from '../../features/users/service/users-service';

function Home() {
    const [ projects, setProjects ] = useState<any[]>([]);
    const [ isLoading, setIsLoading ] = useState(true);
    const [ filterValue, setFilterValue ] = useState('');
    const [ assignedProjectIds, setAssignedProjectIds ] = useState<string[]>([]);
    const [ isSecretariaUser, setIsSecretariaUser ] = useState(false);
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();

    // Função para garantir tempo mínimo de loading
    const setExtendedLoading = (duration: number = 800) => {
        // Limpa qualquer timeout existente
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }
        
        // Ativa o loading
        setIsLoading(true);
        
        // Define um novo timeout que vai desativar o loading após a duração especificada
        loadingTimeoutRef.current = setTimeout(() => {
            setIsLoading(false);
            loadingTimeoutRef.current = null;
        }, duration);
    };

    // Limpa o timeout quando o componente é desmontado
    useEffect(() => {
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, []);

    // Efeito para carregar projetos iniciais
    useEffect(() => {
        const fetchProjects = async () => {
            setExtendedLoading();
            
            try {
                const projects = await fetchProjectsGroupedService();
                setProjects(projects);
            } catch (error) {
                console.log(error);
                setIsLoading(false);
            }
        };
    
        fetchProjects();
    }, []);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const storedUser = JSON.parse(storage.getStorage('user') || '{}');

                if (!storedUser?.id) {
                    return;
                }

                const currentUser = await fetchUserService(storedUser.id);
                const roleName = currentUser?.role?.name?.toLowerCase() || '';
                const nextAssignedProjectIds = currentUser?.projectIds || currentUser?.assignedProjects?.map((project) => project.id) || [];

                setIsSecretariaUser(roleName === 'secretaria');
                setAssignedProjectIds(nextAssignedProjectIds);
            } catch (error) {
                console.log(error);
            }
        };

        fetchCurrentUser();
    }, []);

    // Utilizar debounce para evitar múltiplas requisições rápidas enquanto o usuário digita
    const debounce = (fn: Function, delay: number) => {
        let timer: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), delay);
        };
    };

    // Função para buscar projetos com debounce
    const debouncedFetchProjects = useCallback(
        debounce(async (value: string) => {
            // Ativa o loading com duração mínima garantida
            setExtendedLoading();
            
            try {
                const projects = await fetchProjectsGroupedService(value);
                setProjects(projects);
                // O loading será desativado pelo timeout definido em setExtendedLoading
            } catch (error) {
                console.log(error);
                setIsLoading(false);
            }
        }, 500), // 500ms de delay para permitir digitação contínua
        []
    );

    const handleRedirect = (id: string) => {
        navigate(`/projeto-list/${id}`);
    }

    const handleChange = ({ target: { value }}: any) => {
        setFilterValue(value);
        debouncedFetchProjects(value);
    }

    // Determina se há um filtro ativo
    const isFiltered = filterValue.trim() !== '';

    const isFullWidth = window.innerWidth > 768 ? 900 : window.innerWidth - 40;

    const quickAccessProjects = useMemo(() => {
        if (!isSecretariaUser || !assignedProjectIds.length) {
            return [];
        }

        return projects
            .flatMap((group) =>
                (group.projects || []).map((project: any) => ({
                    id: project.id,
                    name: project.name,
                    principalInvestigatorName: group.principalInvestigator?.name || '',
                }))
            )
            .filter((project) => assignedProjectIds.includes(project.id));
    }, [assignedProjectIds, isSecretariaUser, projects]);

    return (
        <div className="container-home">
            <MenuTop />
            {isLoading && <Loader />}
            <div className='container-home__content'>
                <div className="container-home__content__filter">
                    <Input 
                        placeholder="Filtrar projetos" 
                        onChange={handleChange} 
                        icon={true} 
                        value={filterValue}
                        width={isFullWidth}
                    />
                </div>
                {isSecretariaUser && quickAccessProjects.length > 0 && (
                    <div className="home-quick-access">
                        <div className="home-quick-access__header">
                            <h3>Projetos com acesso</h3>
                        </div>
                        <div className="home-quick-access__grid">
                            {quickAccessProjects.map((project) => (
                                <button
                                    key={project.id}
                                    type="button"
                                    className="home-quick-access__card"
                                    onClick={() => handleRedirect(project.id)}
                                >
                                    <span className="home-quick-access__title">{project.name}</span>
                                    <span className="home-quick-access__pi">
                                        {project.principalInvestigatorName}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className={`accordion-wrapper ${isLoading ? 'hidden' : 'visible'}`}>
                    <AccordionComponent 
                        data={projects} 
                        handleRedirect={handleRedirect} 
                        isFiltered={isFiltered} 
                    />
                </div>
            </div>
        </div>
    );
}

export default Home;
